import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPrice, isPriceOnRequest } from '../../utils/propertyHelpers';
import { useTranslation } from '../../utils/translations';
import { agentsAPI } from '../../services/api';
import { formatInstagramDisplay, getInstagramUrl } from '../../utils/instagramHelpers';
import { getPhoneUrl, getEmailUrl, getWhatsAppUrl } from '../../utils/contactHelpers';
import { getListingStatusLabel, resolveListingStatus } from '../../constants/listingStatus';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import AgentPhoto from '../AgentPhoto/AgentPhoto';
import InstagramIcon from '../InstagramIcon/InstagramIcon';
import { SkeletonAgent } from '../loading';
import './PropertyDetails.css';

const PropertyDetails = ({ property }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [agent, setAgent] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const videoRef = useRef(null);

  const {
    id,
    title,
    location,
    address,
    price,
    currency,
    area,
    bedrooms,
    bathrooms,
    floor,
    furnished,
    elevator,
    hipoteke,
    exclusive,
    parkingIncluded,
    propertyType,
    transactionType,
    images,
    video,
    description,
    agentId,
  } = property;

  const validImages = useMemo(
    () => (images || []).filter((img) => img && img.trim() !== ''),
    [images]
  );
  const hasVideo = Boolean(video);
  const videoIndex = hasVideo ? validImages.length : -1;
  const galleryCount = validImages.length + (hasVideo ? 1 : 0);
  const isVideoSelected = hasVideo && selectedGalleryIndex === videoIndex;

  useEffect(() => {
    if (!agentId) {
      setAgent(null);
      return;
    }

    let cancelled = false;

    const fetchAgent = async () => {
      try {
        setAgentLoading(true);
        const agentData = await agentsAPI.getById(agentId);
        if (!cancelled) {
          setAgent(agentData);
        }
      } catch (error) {
        console.error('Error fetching property agent:', error);
        if (!cancelled) {
          setAgent(null);
        }
      } finally {
        if (!cancelled) {
          setAgentLoading(false);
        }
      }
    };

    fetchAgent();

    return () => {
      cancelled = true;
    };
  }, [agentId]);

  useEffect(() => {
    if (selectedGalleryIndex >= galleryCount) {
      setSelectedGalleryIndex(0);
    }
  }, [galleryCount, selectedGalleryIndex]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (!isVideoSelected) {
      videoEl.pause();
      return;
    }

    const playVideo = () => {
      videoEl.play().catch(() => {});
    };

    if (videoEl.readyState >= 2) {
      playVideo();
    } else {
      videoEl.addEventListener('loadeddata', playVideo, { once: true });
      return () => videoEl.removeEventListener('loadeddata', playVideo);
    }
  }, [isVideoSelected, video]);

  const goToPrevious = () => {
    setSelectedGalleryIndex((prev) => (prev === 0 ? galleryCount - 1 : prev - 1));
  };

  const goToNext = () => {
    setSelectedGalleryIndex((prev) => (prev === galleryCount - 1 ? 0 : prev + 1));
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const goToPreviousLightbox = () => {
    setLightboxIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
  };

  const goToNextLightbox = () => {
    setLightboxIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if (!isLightboxOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsLightboxOpen(false);
      } else if (event.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
      } else if (event.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLightboxOpen, validImages.length]);

  const getPropertyTypeLabel = (type) => {
    return t(`propertyTypes.${type}`) || type;
  };

  const getTransactionTypeLabel = (type) => {
    return t(`transactionTypes.${type}`) || type;
  };

  const whatsappUrl = useMemo(() => {
    if (!agent?.mobile) return '';

    const propertyPageUrl = `${window.location.origin}/property/${id}?share=1`;
    const message = `${propertyPageUrl}\n\n${t('propertyDetails.whatsappMessage')}`;
    return getWhatsAppUrl(agent.mobile, message);
  }, [agent, id, t]);

  const listingStatus = resolveListingStatus(property);
  const listingStatusLabel = getListingStatusLabel(listingStatus, t);

  return (
    <div className="property-details-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <button onClick={() => navigate('/')} className="breadcrumb-link">
            {t('header.home')}
          </button>
          <span className="breadcrumb-separator">/</span>
          <button onClick={() => navigate('/')} className="breadcrumb-link">
            {t('header.properties')}
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{title}</span>
        </nav>

        {/* Property Header */}
        <div className="property-header">
          <div className="property-header-info">
            <h1 className="property-title-large">{title}</h1>
            <p className="property-address">{address || location}</p>
            <p className="property-id">Property ID: {id}</p>
          </div>
        </div>

        {/* Main Image Gallery */}
        <div className="property-gallery">
          <div className="main-image-container">
            {isVideoSelected ? (
              <video
                ref={videoRef}
                src={resolveMediaUrl(video)}
                controls
                autoPlay
                playsInline
                className="gallery-main-video"
              />
            ) : validImages.length > 0 ? (
              <button
                type="button"
                className="main-image-zoom-btn"
                onClick={() => openLightbox(selectedGalleryIndex)}
                aria-label={t('propertyDetails.zoomImage')}
              >
                <img
                  src={resolveMediaUrl(validImages[selectedGalleryIndex])}
                  alt={title}
                  className="main-image"
                  loading="eager"
                  decoding="async"
                  onLoad={(e) => e.currentTarget.classList.add('is-loaded')}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.nextElementSibling.style.display = 'flex';
                  }}
                />
              </button>
            ) : null}
            <div
              className="no-image-placeholder"
              style={{
                display: validImages.length > 0 || isVideoSelected ? 'none' : 'flex',
              }}
            >
              <div className="placeholder-icon">🏠</div>
              <p>{t('propertyDetails.noImage') || 'No Image Available'}</p>
            </div>
            {listingStatusLabel && (
              <span className={`listing-status-badge listing-status-badge--${listingStatus}`}>
                {listingStatusLabel}
              </span>
            )}
            {parkingIncluded && (
              <div className="property-details-parking-badge" title={t('propertyDetails.parkingIncluded')}>
                <span className="property-details-parking-badge-inner">
                  <span>{t('propertyCard.parkingIncludedLine1')}</span>
                  <span>{t('propertyCard.parkingIncludedLine2')}</span>
                </span>
              </div>
            )}
            {galleryCount > 1 && (
              <>
                <button
                  type="button"
                  className="gallery-nav-btn gallery-nav-prev"
                  onClick={goToPrevious}
                  aria-label="Previous"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="gallery-nav-btn gallery-nav-next"
                  onClick={goToNext}
                  aria-label="Next"
                >
                  ›
                </button>
              </>
            )}
          </div>
          {galleryCount > 1 && (
            <div className="thumbnail-gallery">
              {validImages.map((img, index) => (
                <button
                  key={`image-${index}`}
                  type="button"
                  className={`thumbnail-btn ${selectedGalleryIndex === index ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedGalleryIndex(index);
                    openLightbox(index);
                  }}
                  aria-label={`${title} ${index + 1}`}
                >
                  <img
                    src={resolveMediaUrl(img)}
                    alt={`${title} ${index + 1}`}
                    className="thumbnail"
                  />
                </button>
              ))}
              {hasVideo && (
                <button
                  type="button"
                  className={`thumbnail-btn video-thumbnail-btn ${isVideoSelected ? 'active' : ''}`}
                  onClick={() => setSelectedGalleryIndex(videoIndex)}
                  aria-label={t('propertyDetails.video')}
                >
                  <span className="video-thumbnail-icon">▶</span>
                  <span className="video-thumbnail-label">{t('propertyDetails.video')}</span>
                </button>
              )}
            </div>
          )}
        </div>

        {isLightboxOpen && validImages.length > 0 && (
          <div
            className="image-lightbox"
            onClick={closeLightbox}
            role="dialog"
            aria-modal="true"
            aria-label={t('propertyDetails.zoomImage')}
          >
            <button
              type="button"
              className="image-lightbox-close"
              onClick={closeLightbox}
              aria-label={t('propertyDetails.closeLightbox')}
            >
              ×
            </button>
            {validImages.length > 1 && (
              <>
                <button
                  type="button"
                  className="image-lightbox-nav image-lightbox-prev"
                  onClick={(event) => {
                    event.stopPropagation();
                    goToPreviousLightbox();
                  }}
                  aria-label={t('propertyDetails.previousImage')}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="image-lightbox-nav image-lightbox-next"
                  onClick={(event) => {
                    event.stopPropagation();
                    goToNextLightbox();
                  }}
                  aria-label={t('propertyDetails.nextImage')}
                >
                  ›
                </button>
              </>
            )}
            <img
              src={resolveMediaUrl(validImages[lightboxIndex])}
              alt={`${title} ${lightboxIndex + 1}`}
              className="image-lightbox-image"
              onClick={(event) => event.stopPropagation()}
            />
            {validImages.length > 1 && (
              <div className="image-lightbox-counter">
                {lightboxIndex + 1} / {validImages.length}
              </div>
            )}
          </div>
        )}

        {/* Property Info Grid */}
        <div className="property-info-grid">
          <div className="property-main-info">
            {/* Price */}
            <div className="price-section">
              <h2 className="price-large">
                {transactionType === 'rent' ? (
                  <>
                    {formatPrice(price, currency, t('propertyCard.priceOnRequest'))}
                    {!isPriceOnRequest(currency) && (
                      <span className="price-period">/{t('propertyCard.month')}</span>
                    )}
                  </>
                ) : (
                  formatPrice(price, currency, t('propertyCard.priceOnRequest'))
                )}
              </h2>
            </div>

            {/* Key Details */}
            <div className="key-details">
              <div className="detail-item">
                <span className="detail-label">{t('propertyDetails.totalArea')}</span>
                <span className="detail-value">{area} m²</span>
              </div>
              {bedrooms && (
                <div className="detail-item">
                  <span className="detail-label">{t('propertyDetails.bedrooms')}</span>
                  <span className="detail-value">{bedrooms}</span>
                </div>
              )}
              {bathrooms && (
                <div className="detail-item">
                  <span className="detail-label">{t('propertyDetails.bathrooms')}</span>
                  <span className="detail-value">{bathrooms}</span>
                </div>
              )}
              {floor && (
                <div className="detail-item">
                  <span className="detail-label">{t('propertyDetails.floor')}</span>
                  <span className="detail-value">{floor}</span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">{t('propertyDetails.type')}</span>
                <span className="detail-value">{getPropertyTypeLabel(propertyType)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t('propertyDetails.furnished')}</span>
                <span className="detail-value">{furnished ? t('common.yes') : t('common.no')}</span>
              </div>
              {elevator !== undefined && (
                <div className="detail-item">
                  <span className="detail-label">{t('propertyDetails.elevator')}</span>
                  <span className="detail-value">{elevator ? t('common.yes') : t('common.no')}</span>
                </div>
              )}
              {hipoteke !== undefined && (
                <div className="detail-item">
                  <span className="detail-label">{t('propertyDetails.hipoteke')}</span>
                  <span className="detail-value">{hipoteke ? t('common.yes') : t('common.no')}</span>
                </div>
              )}
              {exclusive !== undefined && (
                <div className="detail-item">
                  <span className="detail-label">{t('propertyDetails.exclusive')}</span>
                  <span className="detail-value">{exclusive ? t('common.yes') : t('common.no')}</span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">{t('propertyDetails.transactionType')}</span>
                <span className="detail-value">{getTransactionTypeLabel(transactionType)}</span>
              </div>
            </div>

            {/* Description */}
            <div className="description-section">
              <h3>{t('propertyDetails.description')}</h3>
              <p>{description || t('propertyDetails.defaultDescription')}</p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="property-sidebar">
            <div className="agent-card">
              {agentLoading ? (
                <SkeletonAgent />
              ) : agent ? (
                <>
                  <div className="agent-avatar">
                    <AgentPhoto
                      src={agent.image}
                      alt={agent.name}
                      variant="circle"
                      className="agent-avatar-image"
                    />
                  </div>
                  <h4>{agent.name}</h4>
                  <p className="agent-title">{agent.title}</p>
                  {agent.mobile && (
                    <a href={getPhoneUrl(agent.mobile)} className="agent-contact-link">
                      📞 {agent.mobile}
                    </a>
                  )}
                  {agent.email && (
                    <a href={getEmailUrl(agent.email)} className="agent-contact-link">
                      ✉️ {agent.email}
                    </a>
                  )}
                  {agent.instagram && (
                    <a
                      href={getInstagramUrl(agent.instagram)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="agent-contact-link agent-instagram-link"
                    >
                      <InstagramIcon />
                      {formatInstagramDisplay(agent.instagram)}
                    </a>
                  )}
                  <button
                    type="button"
                    className="btn-primary agent-profile-btn"
                    onClick={() => navigate(`/agent/${agent.id}`, { state: { agent } })}
                  >
                    {t('propertyDetails.viewAgent')}
                  </button>
                  {whatsappUrl && (
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-whatsapp agent-whatsapp-btn"
                    >
                      {t('propertyDetails.contactWhatsApp')}
                    </a>
                  )}
                </>
              ) : (
                <>
                  <div className="agent-avatar">👤</div>
                  <h4>Premium Homes</h4>
                  <p>📧 info@premiumhomes.al</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;
